import { useCallback, useEffect, useRef } from 'react'
import { useQuestionsUpdate } from '../api/hooks/Questions/useQuestionsUpdate'
import { useQuizStore } from '../store/useQuizStore'

export const useQuizData = (data) => {
  const {
    originalData,
    curriculum,
    quizData,
    isModified,
    changedFields,
    initializeFromData,
    updateCurriculum,
    updateQuizData,
    resetToOriginal,
    markAsSaved,
    getChangedData,
    existingQuestionImageUrl,
    existingSubQuestionImageUrl,
    existingExplanationImageUrl,
  } = useQuizStore()

  const questionsUpdateMutation = useQuestionsUpdate()

  const urlObjectsRef = useRef([])

  useEffect(() => {
    return () => {
      urlObjectsRef.current.forEach((url) => URL.revokeObjectURL(url))
      urlObjectsRef.current = []
    }
  }, [])

  const createTrackedObjectURL = useCallback((file) => {
    const url = URL.createObjectURL(file)
    urlObjectsRef.current.push(url)
    return url
  }, [])

  useEffect(() => {
    if (data) {
      initializeFromData(data)
    }
  }, [data, initializeFromData])

  const handleLevelChange = async (value) => {
    if (value) {
      updateCurriculum('level', value)
      await updateCurriculumField({ LEVEL: value })
    }
  }

  const handleMainChapterChange = async (value) => {
    updateCurriculum('mainChapter', value)
    await updateCurriculumField({ MAIN: value })
  }

  const handleSubChapterChange = async (value) => {
    updateCurriculum('subChapter', value)
    await updateCurriculumField({ SUB: value })
  }

  const handleUnitChange = async (value) => {
    updateCurriculum('unit', value)
    await updateCurriculumField({ UNIT: value })
  }

  const handleDetailUnitChange = async (value) => {
    updateCurriculum('detailUnit', value)
    await updateCurriculumField({ DETAIL_UNIT: value })
  }

  const handleCountChange = async (value) => {
    const numericValue = value.replace(/[^0-9]/g, '')
    const number = parseInt(numericValue) || 0
    if (numericValue === '' || (number >= 1 && number <= 100)) {
      updateCurriculum('no', number)
    }
  }

  const handleCorrectAnswersChange = useCallback(
    (values) => {
      updateQuizData('correctAnswers', values)
      updateQuizData('correctAnswer', values[0] || '')
    },
    [updateQuizData]
  )

  const handleCheckedStatesChange = useCallback(
    (states) => {
      updateQuizData('checkedStates', states)
      updateQuizData('isCorrect1', states[0] || false)
      updateQuizData('isCorrect2', states[1] || false)
      updateQuizData('isCorrect3', states[2] || false)
      updateQuizData('isCorrect4', states[3] || false)
      updateQuizData('isCorrect5', states[4] || false)
    },
    [updateQuizData]
  )

  const handleAnswerImageFilesChange = useCallback(
    (files) => {
      updateQuizData('answerImageFiles', files)
      updateQuizData('answerImageFile', files[0])
    },
    [updateQuizData]
  )

  const handleTypeChange = (value) => {
    if (value) {
      updateCurriculum('selectedType', value)
      resetQuizData()
    }
  }

  const handleQuestionChange = (value) => {
    updateQuizData('question', value)
  }

  const handleQuestionImageChange = (file) => {
    updateQuizData('questionImageFile', file)
  }

  const handleQuestionImage1Change = (file) => {
    updateQuizData('questionImageFile1', file)
  }

  const handleQuestionImage2Change = (file) => {
    updateQuizData('questionImageFile2', file)
  }

  const handleSubQuestionImageChange = (file) => {
    updateQuizData('subQuestionImageFile', file)
    updateQuizData('questionImageFile2', file)
  }

  const handleCorrectAnswerChange = (value) => {
    updateQuizData('correctAnswer', value)
  }

  const handleAnswerImageChange = (file) => {
    updateQuizData('answerImageFile', file)
  }

  const handleExplanationChange = (value) => {
    updateQuizData('explanation', value)
  }

  const handleExplanationImageChange = (file) => {
    updateQuizData('explanationImageFile', file)
  }

  const handleExistingQuestionImageRemove = useCallback(() => {
    updateQuizData('existingQuestionImageUrl', '')
  }, [updateQuizData])

  const handleExistingSubQuestionImageRemove = useCallback(() => {
    updateQuizData('existingSubQuestionImageUrl', '')
  }, [updateQuizData])

  const handleExistingExplanationImageRemove = useCallback(() => {
    updateQuizData('existingExplanationImageUrl', '')
  }, [updateQuizData])

  const updateCurriculumField = async (updatedFields) => {
    try {
      if (!originalData || typeof originalData.QUESTION_ID !== 'number') return

      const updateData = {
        QUESTION_ID: originalData.QUESTION_ID,
        LEVEL: curriculum.level || originalData.LEVEL || '',
        MAIN: curriculum.mainChapter || originalData.MAIN || '',
        SUB: curriculum.subChapter || originalData.SUB || '',
        UNIT: curriculum.unit || originalData.UNIT || '',
        DETAIL_UNIT: curriculum.detailUnit || originalData.DETAIL_UNIT || '',
        NO: curriculum.no || originalData.NO || 0,
        TITLE: originalData.TITLE || '',
        IMG_URL: originalData.IMG_URL || '',
        ANSWER1: originalData.ANSWER1 || '',
        ANSWER2: originalData.ANSWER2 || '',
        ANSWER3: originalData.ANSWER3 || '',
        ANSWER4: originalData.ANSWER4 || '',
        ANSWER5: originalData.ANSWER5 || '',
        IS_CORRECT_1: quizData.isCorrect1 ?? false,
        IS_CORRECT_2: quizData.isCorrect2 ?? false,
        IS_CORRECT_3: quizData.isCorrect3 ?? false,
        IS_CORRECT_4: quizData.isCorrect4 ?? false,
        IS_CORRECT_5: quizData.isCorrect5 ?? false,
        DESC: originalData.DESC || '',
        DESC_URL: originalData.DESC_URL || '',
        ...updatedFields,
      }

      await questionsUpdateMutation.mutateAsync(updateData)
    } catch (error) {
      console.error('updateCurriculumField 호출 실패:', error)
    }
  }

  const createFormDataWithFiles = useCallback(() => {
    const formData = new FormData()
    if (!originalData) return null

    formData.append('QUESTION_ID', originalData.QUESTION_ID.toString())
    formData.append('LEVEL', curriculum.level || originalData.LEVEL || '')
    formData.append('MAIN', curriculum.mainChapter || originalData.MAIN || '')
    formData.append('SUB', curriculum.subChapter || originalData.SUB || '')
    formData.append('UNIT', curriculum.unit || originalData.UNIT || '')
    formData.append('NO', (curriculum.no || originalData.NO || 0).toString())
    formData.append('TITLE', quizData.question)

    formData.append(
      'ANSWER1',
      quizData.correctAnswers[0] || quizData.correctAnswer || ''
    )
    formData.append('ANSWER2', quizData.correctAnswers[1] || '')
    formData.append('ANSWER3', quizData.correctAnswers[2] || '')
    formData.append('ANSWER4', quizData.correctAnswers[3] || '')
    formData.append('ANSWER5', quizData.correctAnswers[4] || '')

    formData.append('IS_CORRECT_1', (quizData.isCorrect1 ?? false).toString())
    formData.append('IS_CORRECT_2', (quizData.isCorrect2 ?? false).toString())
    formData.append('IS_CORRECT_3', (quizData.isCorrect3 ?? false).toString())
    formData.append('IS_CORRECT_4', (quizData.isCorrect4 ?? false).toString())
    formData.append('IS_CORRECT_5', (quizData.isCorrect5 ?? false).toString())

    formData.append('DESC', quizData.explanation)

    if (quizData.questionImageFile) {
      formData.append('questionImage', quizData.questionImageFile)
    }
    if (quizData.explanationImageFile) {
      formData.append('explanationImage', quizData.explanationImageFile)
    }
    if (quizData.questionImageFile1) {
      formData.append('questionImage1', quizData.questionImageFile1)
    }
    if (quizData.questionImageFile2) {
      formData.append('questionImage2', quizData.questionImageFile2)
    }

    quizData.answerImageFiles.forEach((file, index) => {
      if (file) {
        formData.append(`answerImage${index + 1}`, file)
      }
    })

    return formData
  }, [originalData, curriculum, quizData])

  const handleSaveQuiz = async () => {
    try {
      if (!originalData) return

      // 모든 이미지 URL 처리를 순서대로 진행
      console.log('이미지 업로드 처리 시작...')

      // 1. 질문 이미지 처리
      const uploadedQuestionImageUrl = quizData.questionImageFile
        ? await uploadImage(quizData.questionImageFile)
        : quizData.existingQuestionImageUrl !== undefined
        ? quizData.existingQuestionImageUrl
        : data?.IMG_URL || ''

      // 2. 서브 질문 이미지 처리
      const uploadedSubQuestionImageUrl = quizData.subQuestionImageFile || quizData.questionImageFile2
        ? await uploadImage(quizData.questionImageFile2)
        : quizData.existingSubQuestionImageUrl !== undefined
        ? quizData.existingSubQuestionImageUrl
        : data?.IMG_URL2 || ''

      // 3. 설명 이미지 처리
      const uploadedExplanationImageUrl = quizData.explanationImageFile
        ? await uploadImage(quizData.explanationImageFile)
        : quizData.existingExplanationImageUrl !== undefined
        ? quizData.existingExplanationImageUrl
        : data?.DESC_URL || ''

      // 4. 답안 이미지들 처리
      const uploadedAnswerImageUrls = []
      for (let i = 0; i < quizData.answerImageFiles.length; i++) {
        const file = quizData.answerImageFiles[i]
        if (file) {
          const url = await uploadImage(file)
          uploadedAnswerImageUrls.push(url)
        } else {
          // 새 파일이 없으면 기존 URL 유지 (빈 문자열로 덮어쓰지 않음)
          uploadedAnswerImageUrls.push(quizData.correctAnswers[i] || '')
        }
      }

      // 5. 이미지 답안 여부 확인 (http:// 포함 모든 URL 패턴 체크)
      const isImageUrl = (v) => {
        const s = String(v || '').trim()
        return s.startsWith('/') || s.startsWith('http') || s.includes('/uploads') || /\.(png|jpe?g|gif|webp|svg)$/i.test(s)
      }
      const isImageAnswer =
        quizData.correctAnswers.some(isImageUrl) ||
        uploadedAnswerImageUrls.some(isImageUrl)

      const finalAnswers = isImageAnswer
        ? uploadedAnswerImageUrls
        : quizData.correctAnswers

      // 6. 최종 데이터 구성
      const finalQuizData = {
        QUESTION_ID: originalData.QUESTION_ID,
        LEVEL: curriculum.level || originalData.LEVEL || '',
        MAIN: curriculum.mainChapter || originalData.MAIN || '',
        SUB: curriculum.subChapter || originalData.SUB || '',
        UNIT: curriculum.unit || originalData.UNIT || '',
        DETAIL_UNIT: curriculum.detailUnit || originalData.DETAIL_UNIT || '',
        NO: curriculum.no || originalData.NO || 0,
        TITLE: quizData.question,
        IMG_URL: uploadedQuestionImageUrl,
        IMG_URL2: uploadedSubQuestionImageUrl,
        ANSWER1: finalAnswers[0] || '',
        ANSWER2: finalAnswers[1] || '',
        ANSWER3: finalAnswers[2] || '',
        ANSWER4: finalAnswers[3] || '',
        ANSWER5: finalAnswers[4] || '',
        IS_CORRECT_1: quizData.isCorrect1 ?? false,
        IS_CORRECT_2: quizData.isCorrect2 ?? false,
        IS_CORRECT_3: quizData.isCorrect3 ?? false,
        IS_CORRECT_4: quizData.isCorrect4 ?? false,
        IS_CORRECT_5: quizData.isCorrect5 ?? false,
        DESC: quizData.explanation,
        DESC_URL: uploadedExplanationImageUrl,
      }

      const result = await questionsUpdateMutation.mutateAsync(finalQuizData)

      markAsSaved()
    } catch (error) {
      console.error('=== handleSaveQuiz 에러 상세 ===', error)
      console.error('에러 스택:', error.stack)
      throw error
    }
  }

  const uploadImage = async (file) => {
    const formData = new FormData()
    formData.append('image', file)
    const baseUrl = process.env.REACT_APP_API_URL
    const res = await fetch(`${baseUrl}/img/upload`, {
      method: 'POST',
      body: formData,
    })
    const data = await res.json()
    if (!res.ok || !data.imageUrl) throw new Error('Failed to upload image')
    return data.imageUrl
  }

  const handleSaveChanges = async () => {
    try {
      const changedData = getChangedData()
      if (Object.keys(changedData).length <= 1) return

      await questionsUpdateMutation.mutateAsync(changedData)
      markAsSaved()
    } catch (error) {
      console.error('handleSaveChanges 실패:', error)
    }
  }

  const resetQuizData = useCallback(() => {
    updateQuizData('question', '')
    updateQuizData('questionImageFile', null)
    updateQuizData('correctAnswer', '')
    updateQuizData('explanation', '')
    updateQuizData('explanationImageFile', null)
    updateQuizData('questionImageFile1', null)
    updateQuizData('questionImageFile2', null)
    updateQuizData('answerImageFile', null)

    updateQuizData('correctAnswers', ['', '', '', '', ''])
    updateQuizData('answerImageFiles', [null, null, null, null, null])

    updateQuizData('checkedStates', [false, false, false, false, false])
    updateQuizData('isCorrect1', false)
    updateQuizData('isCorrect2', false)
    updateQuizData('isCorrect3', false)
    updateQuizData('isCorrect4', false)
    updateQuizData('isCorrect5', false)

    updateQuizData('existingQuestionImageUrl', undefined)
    updateQuizData('existingSubQuestionImageUrl', undefined)
    updateQuizData('existingExplanationImageUrl', undefined)
  }, [updateQuizData])

  return {
    curriculum,
    quizData,
    isModified,
    changedFields,
    isLoading: questionsUpdateMutation.isPending,

    handleLevelChange,
    handleMainChapterChange,
    handleSubChapterChange,
    handleUnitChange,
    handleDetailUnitChange,
    handleCountChange,
    handleTypeChange,

    handleQuestionChange,
    handleQuestionImageChange,
    handleQuestionImage1Change,
    handleQuestionImage2Change,
    handleSubQuestionImageChange,
    handleCorrectAnswerChange,
    handleCorrectAnswersChange,
    handleCheckedStatesChange,
    handleAnswerImageChange,
    handleAnswerImageFilesChange,
    handleExplanationChange,
    handleExplanationImageChange,

    handleExistingQuestionImageRemove,
    handleExistingSubQuestionImageRemove,
    handleExistingExplanationImageRemove,

    handleSaveQuiz,
    handleSaveChanges,
    resetToOriginal,
    resetQuizData,
  }
}
